<script setup>
import {ref, onMounted, onBeforeUnmount} from 'vue';

let player = ref(null);
let isPlayerReady = ref(false);
let playing = ref(false);
let duration = ref(0);
let currentTime = ref(0);
let intervalId = ref();
let formattedTime = ref('00:00:00');
let formattedDuration = ref('00:00:00');
let fullscreenWrapper = ref(null);
let isFullscreen = ref(false);

const props = defineProps(['videoId'])

function initPlayer() {
  // eslint-disable-next-line no-undef
  player.value = new YT.Player('player', {
    height: '390',
    width: '640',
    videoId: props.videoId,
    playerVars: {
      'playsinline': 1,
      'controls': 0,
    },
    events: {
      'onReady': onPlayerReady,
      'onStateChange': onPlayerStateChange
    }
  });
}


function onPlayerReady(event) {
  console.log('>>> onPlayerReady: ', event);
  isPlayerReady.value = true;

  // Start the video
  event.target.playVideo();

  // Set the duration
  duration.value = player.value.getDuration();
  formattedDuration.value = formatTime(duration.value);

  // Start the timer
  intervalId.value = setInterval(() => {
    currentTime.value = player.value.getCurrentTime();
    formattedTime.value = formatTime(currentTime.value);
  }, 1000);
}

function onPlayerStateChange(event) {
  console.log('>>> onPlayerStateChange: ', event);

  // Get the current time
  const currentTime = player.value.getCurrentTime();
  console.log('>>> currentTime: ', currentTime);

  // Set the playing bool
  const playerState = player.value.getPlayerState();
  // console.log('>>> playerState: ', playerState);
  switch (playerState) {
    case 1:
      playing.value = true;
      break;
    default:
      playing.value = false;
  }
}

function playPauseVideo() {
  console.log('>>> playVideo');
  if (playing.value) {
    player.value.pauseVideo();
  } else {
    player.value.playVideo();
  }
}

function seekVideo() {
  player.value.seekTo(currentTime.value, true);
}

function formatTime(number) {
  let hours = Math.floor(number / 3600);
  let minutes = Math.floor((number % 3600) / 60);
  let seconds = Math.floor(number % 60);

  hours = (hours < 10 ? '0' : '') + hours;
  minutes = (minutes < 10 ? '0' : '') + minutes;
  seconds = (seconds < 10 ? '0' : '') + seconds;

  return hours + ':' + minutes + ':' + seconds;
}

const toggleFullscreen = () => {
  if (!document.fullscreenElement) {
    fullscreenWrapper.value.requestFullscreen().catch(err => {
      alert(`Error attempting to enable full-screen mode: ${err.message} (${err.name})`);
    });
    isFullscreen.value = true;
  } else {
    document.exitFullscreen();
    isFullscreen.value = false;
  }
};

onMounted(() => {
  let tag = document.createElement('script');
  tag.src = "https://www.youtube.com/iframe_api";
  let firstScriptTag = document.getElementsByTagName('script')[0];
  firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
  window.onYouTubeIframeAPIReady = initPlayer;
  // check if YT is already loaded
  if (window.YT) {
    initPlayer();
  }
});

onBeforeUnmount(() => {
  if (isPlayerReady.value && player.value) {
    player.value.destroy();
  }
})
</script>

<template>
  <div ref="fullscreenWrapper" class="fullscreen-wrapper">
    <div class="player-wrapper" :class="{'player-wrapper-fullscreen' : isFullscreen}" @click="playPauseVideo">
      <div id="player" class="player-video"></div>
    </div>

    <div class="control-wrapper">
      <input class="progress-bar" :class="'slider'" type="range" min="0" :max="duration" v-model.number="currentTime"
             @input="seekVideo">

      <div class="control-row">
        <div class="button-row">
          <button @click="playPauseVideo" class="play-pause-button" :class="'control-button'">
            <div class="material-symbols-outlined">
              {{ playing ? 'pause' : 'play_arrow' }}
            </div>
          </button>

          <div>
            {{ formattedTime }} / {{ formattedDuration }}
          </div>
        </div>

        <button class="material-symbols-outlined" :class="'control-button'" @click="toggleFullscreen">
          fullscreen
        </button>
      </div>
    </div>
  </div>
</template>

<style>
.fullscreen-wrapper {
  display: flex;
  flex-direction: column;
}

.player-wrapper {
  position: relative;
  width: 100%;
  padding-bottom: 56.25%;
  margin-bottom: 8px;
}

.player-wrapper-fullscreen {
  padding-bottom: 0;
  height: 100%;
}

.player-video {
  pointer-events: none;
  position: absolute;
  width: 100%;
  height: 100%;
}

.progress-bar {
  width: 100%;
  margin-bottom: 8px;
}

.play-pause-button {
  margin-right: 8px;
}

.control-wrapper {
  padding: 12px;
}

.control-row {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
}

.button-row {
  display: flex;
  flex-direction: row;
  justify-content: start;
}

.control-button {
  outline: none;
  background-color: transparent;
  color: grey;
  border: none;
  cursor: pointer;
}

.control-button:hover {
  outline: none !important;
  color: white;
}

.control-button:active {
  outline: none !important;
}

.control-button:focus {
  outline: none;
  box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.1) !important;
}

/* Slider */

.slider {
  -webkit-appearance: none;
  appearance: none;
  width: 100%;
  cursor: pointer;
  outline: none;
  overflow: hidden;
  border-radius: 16px;
}

.slider::-webkit-slider-runnable-track {
  height: 15px;
  background: #ccc;
  border-radius: 16px;
}

.slider::-moz-range-track {
  height: 15px;
  background: #ccc;
  border-radius: 16px;
}

.slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  height: 15px;
  width: 15px;
  background-color: #fff;
  border-radius: 50%;
  border: 2px solid #b176b7;
  box-shadow: -9997px 0 0 9990px #b176b7;
}


.slider::-moz-range-thumb {
  height: 15px;
  width: 15px;
  background-color: #fff;
  border-radius: 50%;
  border: 1px solid #b176b7;
  box-shadow: -9997px 0 0 9990px #b176b7;
}
</style>
